/**
 * Notification Context
 * Provides real-time notification state management with polling
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import notificationService from '../services/notificationService';

// Create context
const NotificationContext = createContext(null);

// Polling interval in milliseconds (30 seconds)
const POLLING_INTERVAL = 30000;

/**
 * Check if user is authenticated
 */
const isAuthenticated = () => {
  const token = localStorage.getItem('authToken');
  return !!token;
};

/**
 * Get current selected role from localStorage
 */
const getSelectedRole = () => {
  return localStorage.getItem('selectedRole') || null;
};

/**
 * NotificationProvider Component
 * Wraps the app to provide notification state and methods
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [currentRole, setCurrentRole] = useState(getSelectedRole());
  
  // Ref to track if component is mounted
  const isMounted = useRef(true);
  const pollingIntervalRef = useRef(null);
  const lastRoleRef = useRef(currentRole);

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = useCallback(async (showLoading = false) => {
    if (!isMounted.current) return;
    
    // Don't fetch if user is not authenticated
    if (!isAuthenticated()) {
      // Clear notifications if user logged out
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    
    try {
      if (showLoading) setLoading(true);
      setError(null);
      
      // Get the currently selected role for filtering
      const role = getSelectedRole();
      
      const result = await notificationService.getNotifications({
        page: 1,
        limit: 20,
        unread_only: false,
        role: role  // Pass role to filter notifications by recipient role
      });
      
      if (isMounted.current) {
        setNotifications(result.notifications || []);
        setUnreadCount(result.unread_count || 0);
        setLastFetched(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      // Don't set error for network issues during polling - just log it
      if (showLoading && isMounted.current) {
        setError('Failed to load notifications');
      }
    } finally {
      if (isMounted.current && showLoading) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Fetch just the unread count (lightweight)
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!isMounted.current) return;
    
    // Don't fetch if user is not authenticated
    if (!isAuthenticated()) return;
    
    try {
      // Get the currently selected role for filtering
      const role = getSelectedRole();
      const count = await notificationService.getUnreadCount(role);
      if (isMounted.current) {
        setUnreadCount(count);
      }
    } catch (err) {
      // Silently fail for count updates
      console.debug('Failed to fetch unread count:', err);
    }
  }, []);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      
      // Decrease unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      return true;
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      return false;
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      // Get the currently selected role for filtering
      const role = getSelectedRole();
      const result = await notificationService.markAllAsRead(role);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
      
      return result.marked_count;
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      return 0;
    }
  }, []);

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Remove from local state
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Decrease unread count if it was unread
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      return true;
    } catch (err) {
      console.error('Failed to delete notification:', err);
      return false;
    }
  }, [notifications]);

  /**
   * Refresh notifications manually
   */
  const refresh = useCallback(() => {
    return fetchNotifications(true);
  }, [fetchNotifications]);

  // Start polling on mount, stop on unmount
  useEffect(() => {
    isMounted.current = true;
    
    // Initial fetch
    fetchNotifications(true);
    
    // Start polling
    pollingIntervalRef.current = setInterval(() => {
      fetchNotifications(false);
    }, POLLING_INTERVAL);
    
    // Cleanup
    return () => {
      isMounted.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchNotifications]);

  // Listen for role changes (check localStorage periodically and on storage events)
  useEffect(() => {
    // Check for role changes every 500ms (handles same-tab changes)
    const roleCheckInterval = setInterval(() => {
      const newRole = getSelectedRole();
      if (newRole !== lastRoleRef.current) {
        console.log(`📢 Role changed from ${lastRoleRef.current} to ${newRole}, refetching notifications`);
        lastRoleRef.current = newRole;
        setCurrentRole(newRole);
        // Refetch notifications with new role
        fetchNotifications(true);
      }
    }, 500);

    // Also listen for storage events (handles cross-tab changes)
    const handleStorageChange = (e) => {
      if (e.key === 'selectedRole') {
        const newRole = e.newValue;
        if (newRole !== lastRoleRef.current) {
          console.log(`📢 Role changed (storage event) to ${newRole}, refetching notifications`);
          lastRoleRef.current = newRole;
          setCurrentRole(newRole);
          fetchNotifications(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(roleCheckInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchNotifications]);

  // Context value
  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    lastFetched,
    currentRole,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    fetchUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Custom hook to use notification context
 * @returns {Object} Notification context value
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  
  return context;
};

export default NotificationContext;
